/**
 * Interactive Reading grader — EAA-1755.
 *
 * Two shapes:
 *  - Reading/listening material (autoGrade = false): nothing to grade; the
 *    activity is complete once viewed, so it scores full marks.
 *  - Pronunciation assessment (autoGrade = true): the grader performs the
 *    SpeechAce scoring itself (async) so ContentPlayer awaits it on submit and
 *    persists the real score. Running it here — rather than in the player after
 *    submit — is what makes the score save to the session and show in history.
 */

import type {
    ContentGrader,
    GradingContext,
    GradingResult,
    QuestionCounter,
} from '@ai-content-hub/content-core';
import { runGradingSafely } from '@ai-content-hub/content-core';
import { speakingScoreService } from '@ai-content-hub/shared-api';
import type {
    InteractiveReadingAnswer,
    InteractiveReadingData,
    InteractiveReadingScoringConfig,
} from './schema';
import { normalizeReadingScore } from './grading';

function hasRecording(answer: InteractiveReadingAnswer | undefined): boolean {
    return typeof answer?.recordingDataUrl === 'string' && answer.recordingDataUrl.length > 0;
}

export async function gradeInteractiveReading(
    data: InteractiveReadingData,
    answer: InteractiveReadingAnswer | undefined,
    scoring?: InteractiveReadingScoringConfig,
    context?: GradingContext
): Promise<GradingResult> {
    // Listening material: no assessment — completing = full marks.
    if (!data.autoGrade) {
        return { isCorrect: true, score: 100, max: 100, details: { material: true } };
    }

    const passingThreshold = scoring?.passingThreshold ?? 0.7;
    const text = (data.text || '').trim();

    // Prefer a score already captured on the answer (e.g. re-grade); otherwise
    // call the async SpeechAce scoring using the learner's recording.
    let raw = answer?.scoreResult as Record<string, unknown> | undefined;
    // EAA-2117: a scoring failure used to be swallowed into a silent 0/100, which the
    // learner/author could not tell from a real zero. It now rejects like every other
    // network grader, so ContentPlayer shows the error and lets them retry instead of
    // persisting a fake score. `tenantId` lets SpeechAce run for an unsaved draft, whose
    // client id the service no longer sends as `content_id`.
    if (!raw && hasRecording(answer) && text && context?.contentId) {
        const blob = await (await fetch(answer!.recordingDataUrl as string)).blob();
        raw = (await speakingScoreService.score(
            {
                text,
                audio: blob,
                // no_mc=1 when the script spans multiple lines (agent contract).
                noMc: text.includes('\n') ? 1 : 0,
                contentId: context.contentId,
                tenantId: context.tenantId,
            },
            context.sessionToken
        )) as unknown as Record<string, unknown>;
    }

    // No recording / no script / no content id → 0 (validateAnswer already gates submit).
    if (!raw) {
        return {
            isCorrect: false,
            score: 0,
            max: 100,
            details: { material: false, recorded: hasRecording(answer) },
        };
    }

    const normalized = normalizeReadingScore(raw, passingThreshold);
    return {
        isCorrect: normalized.passed,
        score: normalized.overall,
        max: 100,
        details: { material: false, recorded: true, scoreResult: raw },
    };
}

export const interactiveReadingContentGrader: ContentGrader<
    InteractiveReadingData,
    InteractiveReadingAnswer
> = {
    requiresNetwork: (data) => Boolean(data.autoGrade),
    // ELMS-2369: SpeechAce/network failures never show server text to the learner.
    grade: (data, answer, cfg, context) =>
        runGradingSafely('interactive-reading', () =>
            gradeInteractiveReading(
                data,
                answer ?? undefined,
                cfg as InteractiveReadingScoringConfig | undefined,
                context
            )
        ),
    validateAnswer: (answer, content) => {
        // Material has no answer requirement; assessment needs a recording.
        if (!content.autoGrade) return true;
        return hasRecording(answer as InteractiveReadingAnswer | undefined);
    },
};

/** Each interactive-reading activity is a single gradable item. */
export function countInteractiveReadingQuestions(): number {
    return 1;
}

export const interactiveReadingQuestionCounter: QuestionCounter<InteractiveReadingData> = {
    countQuestions: countInteractiveReadingQuestions,
};
